import React, { useRef } from "react";
import "./PitchDeckAnalyzer.css";
import BackgroundImage from "../../assets/images/PitchDeckAnalyzerImage.webp";
import { strings } from "../../translations/strings";
import ResponseLoader from "../../common/ResponseLoader";
import Disclaimer from "../../common/Disclaimer";
import BackIcon from "../../common/BackIcon";
import UploadIcon from "../../assets/icons/UploadIcon.svg";
import PdfIcon from "../../assets/icons/PdfIcon.svg";
import AnalyzeIcon from "../../assets/icons/AnalyzeIcon.svg";
import CloseIcon from "../../assets/icons/CloseIcon.svg";
import ResponseIcon from "../../assets/icons/PitchDeckAnalyzer.svg";
import FeedbackButtons from "../../common/FeedbackButtons";
import { transformText } from "../../helpers/functions";
import AskAgainButton from "../../common/AskAgainButton";

const acceptedFormats = [
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
];

const PitchDeckAnalyzer = ({ data }) => {
  const {
    colors,
    goBack,
    isDarkMode,
    isError,
    isLoading,
    aIResponse,
    userFile,
    setUserFile,
    onPressButton,
    onPressReset,
    isPressedFeedback,
    setIsPressedFeedback,
  } = data;
  const fileInputRef = useRef(null);
  const setFile = (file) => {
    if (file?.size > 100 * 1024 * 1024) {
      alert(strings.file_too_big);
    } else if (file && acceptedFormats.includes(file?.type)) {
      setUserFile(file);
    } else {
      alert(strings.file_format_error);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setFile(event?.dataTransfer?.files?.[0]);
  };
  const handleDragOver = (event) => {
    event.preventDefault();
  };
  const handleFileInputChange = (event) => {
    setFile(event?.target?.files?.[0]);
  };
  const deleteFileInput = () => {
    setUserFile(null);
  };

  return (
    <div className="pitch-deck-analyzer-container">
      <img
        alt="Background"
        src={BackgroundImage}
        className="pitch-deck-analyzer-background-image"
        draggable={false}
      />
      <div
        className="pitch-deck-analyzer-hairline"
        style={{ backgroundColor: colors.borderColor }}
      />
      <div
        className="pitch-deck-analyzer-content"
        style={{ backgroundColor: colors.componentBackground }}
      >
        <div className="pitch-deck-analyzer-back-icon-container">
          <BackIcon colors={colors} onPress={goBack} isDarkMode={isDarkMode} />
        </div>
        {isLoading ? (
          <ResponseLoader colors={colors} />
        ) : (
          <>
            <span
              className="pitch-deck-analyzer-title"
              style={{ color: colors.primaryText }}
            >
              {strings.pitch_deck_analyzer_title}
            </span>
            <span
              className="pitch-deck-analyzer-text"
              style={{ color: colors.secondaryText }}
            >
              {strings.pitch_deck_analyzer_text}
            </span>
            <div className="pitch-deck-analyzer-disclaimer-container">
              <Disclaimer colors={colors} />
            </div>
            {aIResponse ? (
              <div className="pitch-deck-analyzer-file-container">
                <div className="pitch-deck-analyzer-pdf-container">
                  <img alt="Pdf" src={PdfIcon} draggable={false} />
                </div>
                <span
                  className="pitch-deck-analyzer-pdf-title"
                  style={{ color: colors.primaryText }}
                >
                  {userFile?.name}
                </span>
                <div
                  className="pitch-deck-analyzer-response-container"
                  style={{ borderColor: colors.borderColor }}
                >
                  <div className="pitch-deck-analyzer-response-icon-and-text">
                    <img
                      alt="Response"
                      src={ResponseIcon}
                      draggable={false}
                      className="pitch-deck-analyzer-response-icon"
                    />
                    <span
                      style={{
                        color: isError ? "#FF3E3E" : colors.primaryText,
                      }}
                      className="pitch-deck-analyzer-response-text"
                    >
                      {transformText(aIResponse)}
                    </span>
                  </div>
                  {!isError && (
                    <FeedbackButtons
                      isDarkMode={isDarkMode}
                      colors={colors}
                      type={strings.pitch_deck_analyzer_type}
                      isPressed={isPressedFeedback}
                      setIsPressed={setIsPressedFeedback}
                    />
                  )}
                </div>
                <AskAgainButton onPress={onPressReset} />
              </div>
            ) : (
              <>
                {userFile ? (
                  <div className="pitch-deck-analyzer-file-container">
                    <div className="pitch-deck-analyzer-pdf-container">
                      <img alt="Pdf" src={PdfIcon} draggable={false} />
                      <div
                        className="pitch-deck-analyzer-pdf-close-button"
                        onClick={deleteFileInput}
                      >
                        <img alt="Close" src={CloseIcon} draggable={false} />
                      </div>
                    </div>
                    <span
                      className="pitch-deck-analyzer-pdf-title"
                      style={{ color: colors.primaryText }}
                    >
                      {userFile?.name}
                    </span>
                    <div
                      className="pitch-deck-analyzer-analyze-button"
                      onClick={onPressButton}
                    >
                      <img alt="Analyze" src={AnalyzeIcon} draggable={false} />
                      <span className="pitch-deck-analyzer-analyze-button-text">
                        {strings.analyze_text}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div
                    className="pitch-deck-analyzer-upload-container"
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onClick={() => fileInputRef?.current?.click()}
                  >
                    <div className="pitch-deck-analyzer-upload-content-container">
                      <input
                        type="file"
                        accept=".doc, .docx, .pdf, .pptx, .txt"
                        style={{ display: "none" }}
                        onChange={handleFileInputChange}
                        ref={fileInputRef}
                      />
                      <div className="pitch-deck-analyzer-upload-image">
                        <img alt="Upload" src={UploadIcon} draggable={false} />
                      </div>
                      <span className="pitch-deck-analyzer-upload-title">
                        {strings.upload_pitch_deck_text}
                      </span>
                      <span className="pitch-deck-analyzer-upload-text">
                        {strings.drag_and_drop_text}
                      </span>
                      <span className="pitch-deck-analyzer-supported_files">
                        {strings.supported_files_text}
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
export default PitchDeckAnalyzer;
